/**
 * Initialization - Funktioner för att initialisera kartan och ladda data
 * 
 * Konverterad från InitializationManager.js till funktioner
 */

import * as districtCache from './district-cache';

/**
 * Ladda initial data från API (units och distrikt) och spara i cache
 * @param {Object} params - Parametrar
 * @param {Object} params.unitsService - UnitsService-instans
 * @param {Object} params.mapService - MapService-instans (för API-anrop)
 * @param {string} params.type - District type
 * @param {Object} params.match - React Router match object
 * @returns {Promise<void>}
 */
export async function loadInitialData({
    unitsService,
    mapService,
    type,
    match
}) {
    
   const districts = districtCache.getAllDistrictsForScenario();
   if (districts && districts.length > 0) {
    return;
   }

    // 2. Hämta initial data parallellt (units och aktiva distrikt)
    // Detta säkerställer att all data är tillgänglig när kartan laddas
    const [units, activeDistrictsResponse] = await Promise.all([
        unitsService.fetchUnits(type),
        fetchActiveDistricts(mapService, type, match.params.scenarioId)
    ]);

    // 4. Ladda alla aktiva distrikt i cache (workaround tills API levererar mer data)
    if (activeDistrictsResponse && activeDistrictsResponse.active && activeDistrictsResponse.active.length > 0) {
        await loadAllDistrictsInCache(activeDistrictsResponse.active, units, mapService, type);
    }
}

/**
 * Hämta aktiva distrikt för scenario (workaround tills API levererar mer data)
 * @param {Object} mapService - MapService-instans
 * @param {string} type - District type
 * @param {string|null} scenarioId - Scenario ID
 * @returns {Promise<Object>} - Response med active array
 */
async function fetchActiveDistricts(mapService, type, scenarioId) {
    try {
        const response = await mapService.fetchActiveDistricts(type, scenarioId);
        return response;
    } catch (error) {
        // Returnera tom response vid fel så att initieringen kan fortsätta
        return { active: [] };
    }
}

/**
 * Ladda alla distrikt från listan och spara i district cache
 * @param {Array} activeDistricts - Array av distrikt från activeDistricts API
 * @param {Object} mapService - MapService-instans
 * @param {string|null} scenarioId - Scenario ID
 */
async function loadAllDistrictsInCache(activeDistricts, units, mapService, type) {
    try {
        // Ladda alla distrikt parallellt (men begränsa till 5 samtidigt för att inte överbelasta)
        const batchSize = 5;
        for (let i = 0; i < activeDistricts.length; i += batchSize) {
            const batch = activeDistricts.slice(i, i + batchSize);
            await Promise.all(
                batch.map(async (districtSummary) => {
                    try {
                        // Hämta fullständig distrikt-data från API
                        const fullDistrict = await mapService.fetchDistrict(districtSummary.id);

                        //TODO: Se till att active district och fulladistriktet har samma data. Så vi slipper en  kombination av de båda
                        const { 
                            salesDistrictArea, 
                            numberOfHits, 
                            turnOver, 
                            salesArea, 
                            vistingTimeHours,
                            frequency
                        } = activeDistricts.find(d => d.id === districtSummary.id);

                        const calculations = {
                            salesDistrictArea: (salesDistrictArea || 0) * 1000000,
                            numberOfHits,
                            turnOver,
                            salesArea,
                            vistingTimeHours,
                            frequency
                        };
                       
                        districtCache.set(districtSummary.id, {
                            ...fullDistrict,
                            ...calculations,
                            isDirty: false,
                            visible: true,
                            allUnits: units,
                        });
                    } catch (error) {
                        // Skip district on fetch error
                    }
                })
            );
        }
    } catch (error) {
        // Init failed; cache may be partially filled
    }
}
